import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    PackagePlus,
    ArrowLeft,
    ArrowRight,
    Check,
    Upload,
    Camera,
    Image as ImageIcon,
    X,
    MapPin,
    Calendar,
    CalendarDays,
    ShieldCheck,
    Loader2,
    Sparkles,
} from "lucide-react";
import { LIST_ITEM } from "../lib/testIds";
import { fetchItemsMeta, createItem, uploadImage, suggestItemPrice } from "../lib/api";
import { PARKS, PARK_OTHER } from "../lib/parks";
import AvailabilityCalendar from "../components/AvailabilityCalendar";

const STEPS = [
    { id: 1, label: "Basics" },
    { id: 2, label: "Pricing & pickup" },
    { id: 3, label: "Photos" },
    { id: 4, label: "Preview" },
];

const CONDITION_OPTIONS = [
    { value: "new", label: "New" },
    { value: "like-new", label: "Like new" },
    { value: "good", label: "Good" },
    { value: "fair", label: "Fair" },
];

const CONDITION_LABEL = Object.fromEntries(
    CONDITION_OPTIONS.map((c) => [c.value, c.label]),
);

const today = () => new Date().toISOString().slice(0, 10);

const Stepper = ({ current }) => (
    <ol
        data-testid={LIST_ITEM.stepper}
        className="flex flex-wrap items-center gap-3"
    >
        {STEPS.map((s, idx) => {
            const isDone = current > s.id;
            const isActive = current === s.id;
            return (
                <li
                    key={s.id}
                    data-testid={LIST_ITEM.stepIndicator(s.id)}
                    className="flex items-center gap-2"
                >
                    <span
                        className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-colors ${
                            isDone
                                ? "bg-[#737a3d] text-[#F4F1EA]"
                                : isActive
                                  ? "border-2 border-[#737a3d] bg-[#F4F1EA] text-[#737a3d]"
                                  : "border border-[#2e3220]/20 bg-[#F4F1EA] text-[#5a6142]"
                        }`}
                    >
                        {isDone ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : s.id}
                    </span>
                    <span
                        className={`text-xs font-bold uppercase tracking-widest ${
                            isActive || isDone ? "text-[#737a3d]" : "text-[#5a6142]"
                        }`}
                    >
                        {s.label}
                    </span>
                    {idx < STEPS.length - 1 && (
                        <span className="mx-1 h-px w-6 bg-[#2e3220]/15" />
                    )}
                </li>
            );
        })}
    </ol>
);

const Field = ({ label, hint, optional, required, children }) => (
    <label className="block">
        <div className="mb-1 flex items-end justify-between gap-2">
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                {label}{" "}
                {optional && (
                    <span className="text-[#5a6142]/70 normal-case tracking-normal font-medium">
                        (optional)
                    </span>
                )}
                {required && (
                    <span className="ml-1 text-[#B56C5A]" aria-hidden="true">*</span>
                )}
            </span>
            {hint && (
                <span className="text-[10px] text-[#5a6142]/80">{hint}</span>
            )}
        </div>
        {children}
    </label>
);

const ListItem = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const dropRef = useRef(null);

    const [step, setStep] = useState(1);
    const [categories, setCategories] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [photos, setPhotos] = useState([]); // [{url, path, name}]
    const [uploadingCount, setUploadingCount] = useState(0);
    const [draggingOver, setDraggingOver] = useState(false);
    const [suggestion, setSuggestion] = useState(null); // {suggested_price, min_price, max_price, reasoning}
    const [suggestionLoading, setSuggestionLoading] = useState(false);
    const [suggestionKey, setSuggestionKey] = useState("");

    const [form, setForm] = useState({
        name: "",
        category: "",
        description: "",
        condition: "good",
        price_per_day: "",
        deposit: "",
        location: "",
        park: "",
        park_other: "",
        full_address: "",
        pickup_instructions: "",
    });
    const [blockedDates, setBlockedDates] = useState([]); // YYYY-MM-DD strings

    useEffect(() => {
        fetchItemsMeta()
            .then((m) => {
                setCategories(m.categories || []);
                setForm((f) => (f.category ? f : { ...f, category: m.categories?.[0]?.value || "" }));
            })
            .catch(() => {});
    }, []);

    const onChange = (field) => (e) => {
        setForm((f) => ({ ...f, [field]: e.target.value }));
    };

    // Fetch a fair-price suggestion when entering Step 2 (or whenever the
    // identifying inputs change). Cached server-side so retries are cheap.
    useEffect(() => {
        if (step < 2) return;
        const name = form.name.trim();
        const category = form.category;
        const condition = form.condition;
        if (!name || name.length < 2 || !category) return;
        const key = `${name.toLowerCase()}|${category}|${condition}`;
        if (key === suggestionKey) return; // already fetched
        let cancelled = false;
        setSuggestionLoading(true);
        suggestItemPrice({ name, category, condition })
            .then((data) => {
                if (cancelled) return;
                setSuggestion(data);
                setSuggestionKey(key);
            })
            .catch(() => {
                if (cancelled) return;
                setSuggestion(null);
            })
            .finally(() => {
                if (!cancelled) setSuggestionLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [step, form.name, form.category, form.condition, suggestionKey]);

    const errors = useMemo(() => {
        const e = {};
        if (step >= 1) {
            if (!form.name.trim()) e.name = "Required";
            if (!form.category) e.category = "Required";
            if (!form.description.trim() || form.description.trim().length < 10)
                e.description = "At least 10 characters";
            if (!form.condition) e.condition = "Required";
        }
        if (step >= 2) {
            const p = Number(form.price_per_day);
            if (!form.price_per_day || Number.isNaN(p) || p < 1)
                e.price_per_day = "Set a price of $1 or more";
            if (form.deposit !== "" && Number(form.deposit) < 0)
                e.deposit = "Cannot be negative";
        }
        if (step >= 3) {
            if (photos.length === 0) e.photos = "Add at least one photo";
        }
        return e;
    }, [form, photos, step]);

    const stepValid = useMemo(() => {
        if (step === 1) {
            return !errors.name && !errors.category && !errors.description && !errors.condition;
        }
        if (step === 2) {
            return !errors.price_per_day && !errors.deposit;
        }
        if (step === 3) {
            return !errors.photos;
        }
        return true;
    }, [errors, step]);

    const goNext = () => {
        if (!stepValid) {
            toast.error("Please fix the highlighted fields");
            return;
        }
        setStep((s) => Math.min(STEPS.length, s + 1));
    };
    const goBack = () => setStep((s) => Math.max(1, s - 1));

    const handleFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        if (photos.length + files.length > 6) {
            toast.error("Up to 6 photos per listing");
            return;
        }
        setUploadingCount((n) => n + files.length);
        for (const file of files) {
            try {
                const data = await uploadImage(file);
                setPhotos((p) => [
                    ...p,
                    { url: data.url, path: data.path, name: file.name },
                ]);
            } catch (err) {
                toast.error(`Couldn't upload "${file.name}"`, {
                    description: err?.response?.data?.detail || "Try a smaller JPG/PNG/WEBP.",
                });
            } finally {
                setUploadingCount((n) => Math.max(0, n - 1));
            }
        }
    };

    const removePhoto = (idx) => {
        setPhotos((p) => p.filter((_, i) => i !== idx));
    };

    const onDrop = async (e) => {
        e.preventDefault();
        setDraggingOver(false);
        const files = e.dataTransfer?.files;
        if (files?.length) await handleFiles(files);
    };

    const handlePublish = async () => {
        if (photos.length === 0) {
            toast.error("Add at least one photo before publishing");
            setStep(3);
            return;
        }
        // Resolve the park value: "Other" requires a custom name.
        const parkValue =
            form.park === PARK_OTHER
                ? form.park_other.trim()
                : (form.park || "").trim();
        if (!parkValue) {
            toast.error("Select a park (or enter one under 'Other')");
            return;
        }
        const addressValue = form.full_address.trim();
        const pickupValue = form.pickup_instructions.trim();
        if (!addressValue) {
            toast.error("Full street address is required");
            return;
        }
        if (!pickupValue) {
            toast.error("Pickup instructions are required");
            return;
        }
        try {
            setSubmitting(true);
            const payload = {
                name: form.name.trim(),
                description: form.description.trim(),
                category: form.category,
                condition: form.condition,
                price_per_day: Number(form.price_per_day),
                deposit: form.deposit === "" ? 0 : Number(form.deposit),
                blocked_dates: blockedDates,
                location: parkValue,
                park: parkValue,
                full_address: addressValue,
                pickup_instructions: pickupValue,
                image_url: photos[0]?.url,
                images: photos.map((p) => p.url),
            };
            const created = await createItem(payload);
            toast.success("Listing published!", {
                description: `"${created.name}" is live on Browse. When a neighbor requests it, you'll see it under 'Pending requests' in Lending and get a notification right away.`,
                duration: 8000,
            });
            navigate("/dashboard/listings");
        } catch (err) {
            toast.error("Couldn't publish your listing", {
                description: err?.response?.data?.detail || "Please try again.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    const categoryLabel = (val) =>
        categories.find((c) => c.value === val)?.label || val;

    return (
        <main
            data-testid={LIST_ITEM.root}
            className="relative z-10 mx-auto max-w-4xl px-6 py-14 md:px-10 md:py-20"
        >
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#2e3220]/10 bg-[#F4F1EA] px-3 py-1.5">
                <PackagePlus className="h-3.5 w-3.5 text-[#737a3d]" strokeWidth={2.5} />
                <span className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                    Become a Lendly Neighbor
                </span>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-4xl font-bold tracking-tight text-[#2e3220] sm:text-5xl">
                        List an item
                    </h1>
                    <p className="mt-2 max-w-xl text-base leading-relaxed text-[#5a6142]">
                        Turn the gear in your garage into a small income — and into a favor
                        for the neighbor down the street.
                    </p>
                </div>
            </div>

            <div className="mt-8 rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-6 md:p-8">
                <Stepper current={step} />

                {/* ---------- Step 1 — Basics ---------- */}
                {step === 1 && (
                    <div className="mt-8 grid gap-5 md:grid-cols-2">
                        <Field label="Item name">
                            <input
                                type="text"
                                value={form.name}
                                onChange={onChange("name")}
                                data-testid={LIST_ITEM.nameInput}
                                placeholder="e.g. 18V Cordless Drill"
                                className="w-full rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm font-medium text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            />
                            {errors.name && (
                                <p className="mt-1 text-xs text-[#B56C5A]">{errors.name}</p>
                            )}
                        </Field>

                        <Field label="Category">
                            <select
                                value={form.category}
                                onChange={onChange("category")}
                                data-testid={LIST_ITEM.categorySelect}
                                className="w-full appearance-none rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm font-medium text-[#2e3220] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            >
                                <option value="">Pick a category</option>
                                {categories.map((c) => (
                                    <option key={c.value} value={c.value}>
                                        {c.label}
                                    </option>
                                ))}
                            </select>
                            {errors.category && (
                                <p className="mt-1 text-xs text-[#B56C5A]">{errors.category}</p>
                            )}
                        </Field>

                        <Field
                            label="Description"
                            hint={`${form.description.length} chars`}
                        >
                            <textarea
                                rows={5}
                                value={form.description}
                                onChange={onChange("description")}
                                data-testid={LIST_ITEM.descriptionInput}
                                placeholder="What is it, what's included, anything a neighbor should know?"
                                className="w-full resize-none rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm leading-relaxed text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            />
                            {errors.description && (
                                <p className="mt-1 text-xs text-[#B56C5A]">{errors.description}</p>
                            )}
                        </Field>

                        <Field label="Condition">
                            <select
                                value={form.condition}
                                onChange={onChange("condition")}
                                data-testid={LIST_ITEM.conditionSelect}
                                className="w-full appearance-none rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm font-medium text-[#2e3220] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            >
                                {CONDITION_OPTIONS.map((c) => (
                                    <option key={c.value} value={c.value}>
                                        {c.label}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    </div>
                )}

                {/* ---------- Step 2 — Pricing & pickup ---------- */}
                {step === 2 && (
                    <div className="mt-8 grid gap-5 md:grid-cols-2">
                        <Field label="Price per day ($)">
                            <input
                                type="number"
                                min={1}
                                step="0.5"
                                value={form.price_per_day}
                                onChange={onChange("price_per_day")}
                                data-testid={LIST_ITEM.priceInput}
                                placeholder="6"
                                className="w-full rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm font-medium text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            />
                            {errors.price_per_day && (
                                <p className="mt-1 text-xs text-[#B56C5A]">
                                    {errors.price_per_day}
                                </p>
                            )}
                            <div
                                data-testid={LIST_ITEM.priceSuggestion}
                                className="mt-2 rounded-xl border border-[#737a3d]/20 bg-[#737a3d]/5 px-3 py-2.5 text-xs"
                            >
                                {suggestionLoading && !suggestion ? (
                                    <span className="inline-flex items-center gap-1.5 text-[#737a3d]">
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        Looking up fair rental rates from neighbors…
                                    </span>
                                ) : suggestion ? (
                                    <div className="flex flex-col gap-1.5">
                                        <p className="font-semibold text-[#2e3220]">
                                            Suggested price:{" "}
                                            <span className="text-[#737a3d]">
                                                ${suggestion.min_price.toFixed(
                                                    suggestion.min_price % 1 ? 2 : 0,
                                                )}
                                                {suggestion.max_price > suggestion.min_price
                                                    ? `–$${suggestion.max_price.toFixed(suggestion.max_price % 1 ? 2 : 0)}`
                                                    : ""}
                                                /day
                                            </span>
                                            <span className="ml-1 text-[#5a6142]">
                                                (recommended ${suggestion.suggested_price.toFixed(
                                                    suggestion.suggested_price % 1 ? 2 : 0,
                                                )})
                                            </span>
                                        </p>
                                        {suggestion.reasoning && (
                                            <p className="text-[#5a6142]">
                                                Based on similar items — {suggestion.reasoning}
                                            </p>
                                        )}
                                        <div className="flex flex-wrap items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setForm((f) => ({
                                                        ...f,
                                                        price_per_day: String(
                                                            suggestion.suggested_price,
                                                        ),
                                                    }))
                                                }
                                                data-testid={LIST_ITEM.priceUseSuggestion}
                                                className="inline-flex items-center gap-1 rounded-full bg-[#737a3d] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#F4F1EA] transition-colors hover:bg-[#8a9450]"
                                            >
                                                Use ${suggestion.suggested_price.toFixed(
                                                    suggestion.suggested_price % 1 ? 2 : 0,
                                                )}
                                            </button>
                                            <span className="text-[10px] italic text-[#5a6142]">
                                                This is only a recommendation — you can set any price you&apos;d like.
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <span className="text-[#5a6142]">
                                        We&apos;ll suggest a fair daily price as soon as you fill in the item name.
                                    </span>
                                )}
                            </div>
                        </Field>
                        {/* Deposit field intentionally hidden — deposit
                            flows have been temporarily disabled across the
                            app. Value stays 0 so the backend price
                            breakdown doesn't add anything on top. */}

                        <Field label="Park / neighborhood" required>
                            <select
                                value={form.park}
                                onChange={onChange("park")}
                                data-testid="listitem-park-select"
                                className="w-full rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm font-medium text-[#2e3220] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            >
                                <option value="">Select a park…</option>
                                {PARKS.map((p) => (
                                    <option key={p} value={p}>
                                        {p}
                                    </option>
                                ))}
                                <option value={PARK_OTHER}>Other (fill in)</option>
                            </select>
                            {form.park === PARK_OTHER && (
                                <input
                                    type="text"
                                    value={form.park_other}
                                    onChange={onChange("park_other")}
                                    data-testid="listitem-park-other-input"
                                    placeholder="Enter your park / community name"
                                    className="mt-2 w-full rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm font-medium text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                                />
                            )}
                            <p className="mt-1 text-[11px] text-[#5a6142]">
                                Shown to everyone on Browse. Choose the park you live in.
                            </p>
                        </Field>

                        <Field label="Full street address" required>
                            <input
                                type="text"
                                value={form.full_address}
                                onChange={onChange("full_address")}
                                data-testid="listitem-full-address-input"
                                placeholder="1234 Maple St, Austin TX 78744"
                                className="w-full rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm font-medium text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            />
                            <p className="mt-1 text-[11px] text-[#5a6142]">
                                Kept private. Only shared with a renter after they pay.
                            </p>
                        </Field>
                        <Field label="Pickup instructions" required>
                            <textarea
                                rows={4}
                                value={form.pickup_instructions}
                                onChange={onChange("pickup_instructions")}
                                data-testid={LIST_ITEM.pickupInput}
                                placeholder="e.g. Side gate is unlocked between 9am–6pm — drill is on the workbench."
                                className="w-full resize-none rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm leading-relaxed text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            />
                        </Field>

                        {/* Block out unavailable dates */}
                        <div className="md:col-span-2 mt-2 rounded-3xl border border-[#2e3220]/10 bg-[#E8E2D0]/40 p-5">
                            <div className="flex flex-wrap items-end justify-between gap-3">
                                <div>
                                    <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                        <CalendarDays className="h-3.5 w-3.5" strokeWidth={2.5} />
                                        Unavailable dates
                                    </p>
                                    <h3 className="mt-1 font-display text-base font-bold text-[#2e3220]">
                                        Block any days you can&apos;t lend
                                    </h3>
                                    <p className="mt-1 max-w-md text-xs text-[#5a6142]">
                                        Going out of town or using the item yourself? Tap
                                        any future day to mark it Unavailable. You can edit
                                        this anytime from Lending.
                                    </p>
                                </div>
                                <p className="rounded-full bg-[#737a3d]/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                    {blockedDates.length} day
                                    {blockedDates.length === 1 ? "" : "s"} blocked
                                </p>
                            </div>
                            <div className="mt-4">
                                <AvailabilityCalendar
                                    mode="edit"
                                    blockedDates={blockedDates}
                                    onToggleDate={(iso, becameBlocked) => {
                                        setBlockedDates((prev) =>
                                            becameBlocked
                                                ? [...new Set([...prev, iso])].sort()
                                                : prev.filter((d) => d !== iso),
                                        );
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* ---------- Step 3 — Photos ---------- */}
                {step === 3 && (
                    <div className="mt-8">
                        <div
                            ref={dropRef}
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDraggingOver(true);
                            }}
                            onDragLeave={() => setDraggingOver(false)}
                            onDrop={onDrop}
                            data-testid={LIST_ITEM.photoDropzone}
                            className={`flex flex-col items-center justify-center rounded-3xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
                                draggingOver
                                    ? "border-[#737a3d] bg-[#737a3d]/5"
                                    : "border-[#2e3220]/25 bg-white/40"
                            }`}
                        >
                            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#737a3d] text-[#F4F1EA]">
                                <ImageIcon className="h-6 w-6" strokeWidth={2.25} />
                            </div>
                            <p className="mt-4 font-display text-lg font-bold text-[#2e3220]">
                                Add photos of your item
                            </p>
                            <p className="mt-1 text-xs text-[#5a6142]">
                                JPG, PNG, or WEBP up to 8MB each. Up to 6 photos.
                            </p>
                            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    data-testid="photo-upload-btn"
                                    className="btn-primary !py-2.5 text-sm"
                                >
                                    <Upload className="h-4 w-4" strokeWidth={2.5} />
                                    Upload photos
                                </button>
                                <button
                                    type="button"
                                    onClick={() => cameraInputRef.current?.click()}
                                    data-testid="photo-camera-btn"
                                    className="btn-secondary !py-2.5 text-sm"
                                >
                                    <Camera className="h-4 w-4" strokeWidth={2.5} />
                                    Take photo
                                </button>
                            </div>
                            <p className="mt-3 text-[10px] uppercase tracking-widest text-[#5a6142]/70">
                                or drop them anywhere on this card
                            </p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                multiple
                                onChange={(e) => {
                                    handleFiles(e.target.files);
                                    e.target.value = "";
                                }}
                                data-testid={LIST_ITEM.photoUploadInput}
                                className="hidden"
                            />
                            <input
                                ref={cameraInputRef}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                onChange={(e) => {
                                    handleFiles(e.target.files);
                                    e.target.value = "";
                                }}
                                data-testid="photo-camera-input"
                                className="hidden"
                            />
                        </div>

                        {(photos.length > 0 || uploadingCount > 0) && (
                            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                                {photos.map((p, i) => (
                                    <div
                                        key={p.path}
                                        data-testid={LIST_ITEM.photoThumb(i)}
                                        className="group relative aspect-square overflow-hidden rounded-2xl border border-[#2e3220]/10 bg-[#E8E2D0]"
                                    >
                                        <img
                                            src={p.url}
                                            alt={p.name}
                                            className="h-full w-full object-cover"
                                        />
                                        {i === 0 && (
                                            <span className="absolute left-2 top-2 rounded-full bg-[#737a3d] px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-[#F4F1EA]">
                                                Cover
                                            </span>
                                        )}
                                        <button
                                            type="button"
                                            data-testid={LIST_ITEM.photoRemove(i)}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removePhoto(i);
                                            }}
                                            aria-label="Remove photo"
                                            className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#2e3220]/85 text-[#F4F1EA] opacity-0 transition-opacity hover:bg-[#2e3220] group-hover:opacity-100"
                                        >
                                            <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                                        </button>
                                    </div>
                                ))}
                                {Array.from({ length: uploadingCount }).map((_, i) => (
                                    <div
                                        key={`u-${i}`}
                                        className="flex aspect-square items-center justify-center rounded-2xl border border-dashed border-[#2e3220]/20 bg-[#E8E2D0]/60 text-[#737a3d]"
                                    >
                                        <Loader2 className="h-5 w-5 animate-spin" />
                                    </div>
                                ))}
                            </div>
                        )}
                        {errors.photos && photos.length === 0 && uploadingCount === 0 && (
                            <p className="mt-3 text-xs text-[#B56C5A]">{errors.photos}</p>
                        )}
                    </div>
                )}

                {/* ---------- Step 4 — Preview ---------- */}
                {step === 4 && (
                    <div data-testid={LIST_ITEM.preview} className="mt-8">
                        <div className="inline-flex items-center gap-2 rounded-full bg-[#737a3d]/10 px-3 py-1.5">
                            <Sparkles className="h-3.5 w-3.5 text-[#737a3d]" strokeWidth={2.5} />
                            <span className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                                Final look — make sure it&apos;s right
                            </span>
                        </div>

                        <div className="mt-5 grid gap-6 md:grid-cols-[1.1fr_1fr]">
                            <div className="overflow-hidden rounded-3xl border border-[#2e3220]/10 bg-[#E8E2D0]">
                                {photos[0] ? (
                                    <img
                                        src={photos[0].url}
                                        alt={form.name}
                                        className="aspect-[4/3] w-full object-cover"
                                    />
                                ) : (
                                    <div className="flex aspect-[4/3] items-center justify-center text-[#737a3d]">
                                        <ImageIcon className="h-8 w-8" />
                                    </div>
                                )}
                                {photos.length > 1 && (
                                    <div className="grid grid-cols-5 gap-1 p-1">
                                        {photos.slice(1, 6).map((p) => (
                                            <img
                                                key={p.path}
                                                src={p.url}
                                                alt=""
                                                className="aspect-square w-full rounded-lg object-cover"
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-col gap-4 rounded-3xl border border-[#2e3220]/10 bg-white/40 p-6">
                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                        {categoryLabel(form.category)}
                                    </p>
                                    <h3 className="mt-1 font-display text-2xl font-bold text-[#2e3220]">
                                        {form.name || "Untitled item"}
                                    </h3>
                                    {(form.park === PARK_OTHER
                                        ? form.park_other
                                        : form.park) && (
                                        <p className="mt-1 inline-flex items-center gap-1 text-xs text-[#5a6142]">
                                            <MapPin className="h-3.5 w-3.5" />
                                            {form.park === PARK_OTHER
                                                ? form.park_other
                                                : form.park}
                                        </p>
                                    )}
                                </div>

                                <p className="text-sm leading-relaxed text-[#5a6142]">
                                    {form.description || "—"}
                                </p>

                                <div className="flex flex-wrap gap-4">
                                    <div className="rounded-2xl bg-[#737a3d]/10 px-4 py-3">
                                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                            Daily rate
                                        </p>
                                        <p className="font-display text-2xl font-bold text-[#737a3d]">
                                            ${Number(form.price_per_day || 0).toFixed(2)}
                                        </p>
                                    </div>
                                    <div className="rounded-2xl bg-[#2e3220]/5 px-4 py-3">
                                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#5a6142]">
                                            Deposit
                                        </p>
                                        <p className="font-display text-2xl font-bold text-[#2e3220]">
                                            ${Number(form.deposit || 0).toFixed(2)}
                                        </p>
                                    </div>
                                    <div className="rounded-2xl bg-[#2e3220]/5 px-4 py-3">
                                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#5a6142]">
                                            Condition
                                        </p>
                                        <p className="font-display text-base font-bold text-[#2e3220]">
                                            {CONDITION_LABEL[form.condition] || form.condition}
                                        </p>
                                    </div>
                                </div>

                                <div className="rounded-2xl border border-[#2e3220]/10 bg-[#F4F1EA] px-4 py-3 text-sm">
                                    <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                        <Calendar className="h-3.5 w-3.5" />
                                        Availability
                                    </p>
                                    <p className="mt-1 text-[#2e3220]">
                                        {blockedDates.length
                                            ? `${blockedDates.length} day${blockedDates.length === 1 ? "" : "s"} blocked`
                                            : "No dates blocked"}
                                    </p>
                                </div>

                                {form.pickup_instructions && (
                                    <div className="rounded-2xl border border-[#2e3220]/10 bg-[#F4F1EA] px-4 py-3">
                                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                            Pickup instructions
                                        </p>
                                        <p className="mt-1 text-sm leading-relaxed text-[#2e3220]">
                                            {form.pickup_instructions}
                                        </p>
                                    </div>
                                )}

                                <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-[#737a3d]/10 px-3 py-1.5 text-xs font-bold text-[#737a3d]">
                                    <ShieldCheck className="h-3.5 w-3.5" />
                                    Once published, this listing goes live on Browse instantly.
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ---------- Nav ---------- */}
                <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-[#2e3220]/10 pt-6">
                    <button
                        type="button"
                        onClick={goBack}
                        disabled={step === 1}
                        data-testid={LIST_ITEM.backBtn}
                        className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-[#2e3220]/80 hover:text-[#737a3d] disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <ArrowLeft className="h-4 w-4" strokeWidth={2.5} />
                        Back
                    </button>
                    <p className="text-xs text-[#5a6142]">
                        Step {step} of {STEPS.length}
                    </p>
                    {step < STEPS.length ? (
                        <button
                            type="button"
                            onClick={goNext}
                            data-testid={LIST_ITEM.nextBtn}
                            className="btn-primary !py-2.5 text-sm"
                        >
                            Continue
                            <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={handlePublish}
                            disabled={submitting}
                            data-testid={LIST_ITEM.publishBtn}
                            className="btn-primary !py-2.5 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Publishing…
                                </>
                            ) : (
                                <>
                                    Publish listing
                                    <Check className="h-4 w-4" strokeWidth={2.75} />
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </main>
    );
};

export default ListItem;
